import { DriveStore } from "../../store/reducers";
import * as logger from "../logger";
import { getCurrentUser } from "../storage/storage";
import { observeStore } from "../store";
import { IPerformanceProfiler, IPerformanceBatcher } from "./performanceProfilerInterface";
import { CancelablePromise } from "../../utils/cancelablePromise";
import uuid from "geotab-uuid";
import { MINUTES } from "../../constants";


export default class PerformanceProfiler implements IPerformanceProfiler {
    static readonly timeoutMs = 5 * MINUTES;

    performanceMeasure: PerformanceMeasure;
    readonly startTimeMark: string;
    readonly endTimeMark: string;
    readonly measureName: string;
    private readonly internalMeasureName: string;
    private readonly guid: string;
    private isInProgress = false;
    private isEndInProgress = false;
    private startTimestamp = -1;
    private timeoutId?: ReturnType<typeof setTimeout>;
    private readonly batcher?: IPerformanceBatcher;

    constructor(measureName: string, batcher?: IPerformanceBatcher) {
        this.measureName = measureName;
        this.guid = uuid.uuid();
        this.internalMeasureName = `${ measureName }_${ this.guid }`;
        this.startTimeMark = `${ this.internalMeasureName }_start`;
        this.endTimeMark = `${ this.internalMeasureName }_end`;
        this.batcher = batcher;
    }

    // Profiles an action and ends profiling when the next page is loaded.
    profileActionAndWaitForNextPage(store: DriveStore, nextPage: string) {
        this.start();
        let unsubscribeFromPageName = observeStore(store, state => state.mobile.currentPage, state => {
            if (state === nextPage) {
                this.end();
                unsubscribeFromPageName();
            }
        });
    }

    start(): void {
        if (this.isInProgress) {
            return;
        }
        this.isInProgress = true;
        this.isEndInProgress = false;

        // Clear marks and measures to avoid memory leaks
        this.clearPerformanceEntries();

        this.startTimestamp = performance.now();
        performance.mark(this.startTimeMark);

        // Set timeout to prevent memory leaks from abandoned profilers
        this.timeoutId = setTimeout(() => this.handleTimeout(), PerformanceProfiler.timeoutMs);
    }

    end(): void {
        if (!this.isInProgress || this.isEndInProgress) {
            return;
        }

        try {
            this.isEndInProgress = true;

            // Clear timeout first to prevent the timeout handler from clearing marks
            // between our guard check and the measure call
            this.clearTimeout();

            // Guard: Check if the start mark still exists before attempting to measure
            const startMarkExists = performance.getEntriesByName(this.startTimeMark, "mark").length > 0;
            if (!startMarkExists) {
                // Start mark was cleared (likely by timeout), exit gracefully
                logger.event(
                    logger.Event.PERFORMANCE,
                    `Performance measure '${ this.measureName }' was cancelled before completion (start mark cleared)`
                );
                return;
            }

            const endTimestamp = performance.now();
            performance.mark(this.endTimeMark);
            // Older browsers (Chrome <78, Safari <14.5, Firefox <103) implement User Timing Level 1
            // where performance.measure() returns undefined instead of a PerformanceMeasure.
            // Fall back to reading from the performance timeline buffer via getEntriesByName(),
            // then to manually computed timestamps if the buffer is full.
            let measuredPerformance: PerformanceMeasure | undefined;
            let measureThrew = false;
            try {
                measuredPerformance = performance.measure(this.internalMeasureName, this.startTimeMark, this.endTimeMark) as PerformanceMeasure | undefined;
            } catch {
                // measure() threw — no entry was written to the buffer, so skip
                // the getEntriesByName fallback (tier 2) to avoid picking up a
                // stale entry from a concurrent profiler with the same measureName.
                measureThrew = true;
            }

            if (!measureThrew && !this.isValidPerformanceMeasure(measuredPerformance)) {
                logger.log(logger.Source.PERFORMANCE, `'${ this.measureName }' using getEntriesByName fallback`);
                const entries = performance.getEntriesByName(this.internalMeasureName, "measure");
                measuredPerformance = entries.length > 0 ? entries[entries.length - 1] as PerformanceMeasure : undefined;
            }

            if (!this.isValidPerformanceMeasure(measuredPerformance)) {
                if (this.startTimestamp < 0) {
                    logger.error(logger.Source.PERFORMANCE, `Performance measure '${ this.measureName }' failed: no valid start timestamp`);
                    return;
                }
                logger.log(logger.Source.PERFORMANCE, `'${ this.measureName }' using manual timestamp fallback`);
                measuredPerformance = { duration: endTimestamp - this.startTimestamp, startTime: this.startTimestamp } as PerformanceMeasure;
            }

            this.performanceMeasure = measuredPerformance;

            if (this.batcher) {
                this.batcher.add({
                    name: this.measureName,
                    duration: this.performanceMeasure.duration,
                    startTime: this.performanceMeasure.startTime
                });
            } else {
                logger.event(
                    logger.Event.PERFORMANCE,
                    JSON.stringify({
                        name: this.measureName,
                        duration: (this.performanceMeasure.duration / 1000).toFixed(3),
                        startTime: this.performanceMeasure.startTime
                    }),
                    getCurrentUser(),
                    this.performanceMeasure.duration
                );
            }
        } catch {
            // Silently swallow — failures here are typically caused by performance.mark()
            // being swallowed by the polyfill in default.ts (returning {} without writing
            // to the buffer), which then causes performance.measure() to throw when the
            // start mark cannot be found. Logging this as an error causes noise without
            // actionable signal.
        } finally {
            this.isInProgress = false;
            this.isEndInProgress = false;
            this.clearTimeout();
            this.clearPerformanceEntries();
        }
    }

    /**
     * Cancels profiling without logging or recording measurements.
     * Cleans up performance marks to prevent memory leaks.
     */
    cancel(): void {
        if (!this.isInProgress || this.isEndInProgress) {
            return;
        }

        // Clean up performance marks without error logging
        this.clearPerformanceEntries();

        // Reset state flags
        this.isInProgress = false;
        this.isEndInProgress = false;
        this.startTimestamp = -1;
        this.clearTimeout();
    }

    measure<T>(func: () => T): T {
        this.start();
        try {
            let res = func();
            return res;
        } finally {
            this.end();
        }
    }

    async measureAsync<T>(func: () => Promise<T>): Promise<T> {
        this.start();
        try {
            let res = await func();
            return res;
        } finally {
            this.end();
        }
    }

    measureCancelable<T>(func: () => CancelablePromise<T>): CancelablePromise<T> {
        this.start();
        const promise = func();

        // By wrapping the `cancel` method of a CancelablePromise, we can ensure
        // the profiler's `end()` is called even when the promise is canceled and
        // would otherwise never resolve or reject.
        const originalCancel = promise.cancel.bind(promise);
        promise.cancel = () => {
            originalCancel();
            this.end();
        };

        return promise.then(
            (result) => {
                this.end();
                return result;
            },
            (error) => {
                this.end();
                throw error;
            }
        ).catch((error) => {
            if (this.isInProgress) {
                this.end();
            }
            throw error;
        });
    }

    private handleTimeout(): void {
        if (!this.isInProgress) {
            return;
        }

        logger.event(
            logger.Event.PERFORMANCE,
            `Performance measure '${ this.measureName }' timed out after ${ PerformanceProfiler.timeoutMs / MINUTES } minutes`
        );

        this.cancel();
    }

    private clearTimeout(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = undefined;
        }
    }

    private clearPerformanceEntries(): void {
        performance.clearMarks(this.startTimeMark);
        performance.clearMarks(this.endTimeMark);
        performance.clearMeasures(this.internalMeasureName);
    }

    private isValidPerformanceMeasure(measure: PerformanceMeasure | undefined): measure is PerformanceMeasure {
        if (!measure) {
            return false;
        }

        return Number.isFinite(measure.duration) && Number.isFinite(measure.startTime) && measure.startTime > 0;
    }
}
